import { computed, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import md5 from 'md5';
import { Account, RENAULT_ID } from '../class/Account';
import { Company } from '../class/Company';

export interface AccountConnect {
  save: boolean;
  email: string;
  password: string;
}

/**
 * Holds the connected-account state shared between ConnectionService (owns the
 * login business logic) and LoadingService (owns logout/clear), and the many
 * services/components that only read it, without creating a dependency cycle
 * between them.
 *
 * The signals are read-only from the outside - all writes must go through
 * setAccount/updateAccount/setAccountsList/setCompanyList/setCompany/
 * setAccountConnect/clear so every change to the shared state is traceable to
 * one of these methods.
 */
@Injectable({
  providedIn: 'root',
})
export class AccountStateService {
  private _account = signal<Account>(undefined);
  private _accountsList = signal<Account[] | null>(null);
  private _companyList = signal<Company[] | null>(null);
  private _company = signal<Company>(undefined);
  private _accountConnect = signal<AccountConnect | null>(null);

  public $account = this._account.asReadonly();
  public $accountsList = this._accountsList.asReadonly();
  public $companyList = this._companyList.asReadonly();
  public $company = this._company.asReadonly();
  public $accountConnect = this._accountConnect.asReadonly();
  public $isRenault = computed(() => this._account()?.uid_site == RENAULT_ID);
  public isRenault$ = toObservable(this.$isRenault);
  public $accountHash = computed(() => {
    const account = this._account();
    if (!account) return undefined;

    const company = this._company();
    const a = {
      mail: account.user_mail,
      account: company === undefined ? account.uid_company : company.uid,
      uid: account.uid,
    };

    return md5(JSON.stringify(a));
  });

  public setAccount(account: Account | null): void {
    this._account.set(account);
  }

  public updateAccount(updater: (account: Account) => Account): void {
    this._account.update(updater);
  }

  public setAccountsList(accountsList: Account[] | null): void {
    this._accountsList.set(accountsList);
  }

  public setCompanyList(companyList: Company[] | null): void {
    this._companyList.set(companyList);
  }

  public setCompany(company: Company | undefined): void {
    this._company.set(company);
  }

  public setAccountConnect(accountConnect: AccountConnect | null): void {
    this._accountConnect.set(accountConnect);
  }

  public persistAccountHash(): void {
    try {
      localStorage['accountHash'] = this.$accountHash();
    } catch (error) {
      console.log(error);
    }
  }

  public clear(): void {
    this._account.set(null);
    this._accountsList.set(null);
    this._companyList.set(null);
    this._company.set(undefined);
  }
}
